/**
 * Zähler neuer Online-Bestellungen im Menü (und auf dem Handy oben in der Kopfzeile) – GET /api/webshop/orders/count
 * alle 45 Sekunden, nur mit Recht webshop.orders und nur, solange der Browser-Tab sichtbar ist.
 * Dazu: Hinweis im Fenstertitel („(2) Kiosk Kasse“) und – falls auf diesem Gerät eingeschaltet – ein dezenter Ton.
 * Bewusst klein gehalten: wird mit dem Menü geladen (web/src/modules.tsx), nicht erst mit der Seite.
 */
import { useEffect, useRef } from 'react';
import { NavLink } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ShoppingBag } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Badge } from '../../components/ui';
import { chime, KEYS, soundEnabled, type OrderCounts } from './types';

export function useOrderCounts() {
  const { can, setting } = useAuth();
  const enabled = can('webshop.orders');
  const shopOn = setting<unknown>('webshop.enabled') === true;
  return useQuery({
    queryKey: KEYS.count,
    queryFn: () => api.get<OrderCounts>('/webshop/orders/count'),
    enabled,
    // Shop aus: nur selten nachsehen (es kann noch offene Bestellungen von vorher geben)
    refetchInterval: shopOn ? 45_000 : 300_000,
    refetchIntervalInBackground: false,
    staleTime: 20_000,
    retry: false,
  });
}

const stripCount = (t: string) => t.replace(/^\(\d+\)\s/, '');

export default function WebshopNavBadge({ variant }: { variant: 'nav' | 'topbar' }) {
  const q = useOrderCounts();
  const n = q.data?.new ?? 0;
  const ready = q.data?.ready ?? 0;
  const prev = useRef<number | null>(null);

  // Titel und Ton nur einmal (Menü-Eintrag ist immer vorhanden, auch wenn das Menü auf dem Handy eingeklappt ist)
  useEffect(() => {
    if (variant !== 'nav') return;
    const base = stripCount(document.title);
    document.title = n > 0 ? `(${n}) ${base}` : base;
  }, [n, variant]);
  useEffect(() => () => { if (variant === 'nav') document.title = stripCount(document.title); }, [variant]);
  useEffect(() => {
    if (variant !== 'nav' || q.data == null) return;
    if (prev.current != null && n > prev.current && soundEnabled()) chime();
    prev.current = n;
  }, [n, q.data, variant]);

  if (n === 0) return null;
  const title = `${n} neue Online-Bestellung${n === 1 ? '' : 'en'}${ready ? ` · ${ready} liegen bereit` : ''}`;
  if (variant === 'nav') return <Badge tone="crit" title={title}>{n}</Badge>;
  return (
    <NavLink to="/webshop" style={{ color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 4 }} aria-label={title} title={title}>
      <ShoppingBag size={20} /> <Badge tone="crit">{n}</Badge>
    </NavLink>
  );
}
